import type { ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';
import { translations } from './i18n';

export interface MessageDescriptor {
  id: string;
  defaultMessage?: string;
  values?: Record<string, unknown>;
}

interface IntlShape {
  formatMessage: (descriptor: MessageDescriptor, values?: Record<string, unknown>) => string;
}

const fallbackIntl: IntlShape = {
  formatMessage: ({ id, defaultMessage, values: descriptorValues }, values) =>
    interpolate(defaultMessage ?? id, values ?? descriptorValues),
};

const IntlContext = createContext<IntlShape>(fallbackIntl);

interface IntlProviderProps {
  locale?: keyof typeof translations;
  messages?: Record<string, string>;
  children: ReactNode;
}

const INTERPOLATION_REGEX = /{(\w+)}/g;

export const IntlProvider = ({ locale = 'en', messages, children }: IntlProviderProps) => {
  const resolvedMessages = useMemo<Record<string, string>>(
    () => (messages ? messages : translations[locale] ? translations[locale] : {}),
    [messages, locale]
  );
  const value = useMemo<IntlShape>(
    () => ({
      formatMessage: ({ id, defaultMessage, values: descriptorValues }, values) => {
        const template = resolvedMessages[id] ?? defaultMessage ?? id;
        return interpolate(template, values ?? descriptorValues);
      },
    }),
    [resolvedMessages]
  );

  return <IntlContext.Provider value={value}>{children}</IntlContext.Provider>;
};

export const useIntl = () => useContext(IntlContext);

type FormattedMessageProps = MessageDescriptor & {
  children?: (chunks: string) => ReactNode;
};

export const FormattedMessage = ({ id, defaultMessage, values, children }: FormattedMessageProps) => {
  const { formatMessage } = useIntl();
  const message = formatMessage({ defaultMessage, id }, values);

  if (typeof children === 'function') {
    return <>{children(message)}</>;
  }

  return <>{message}</>;
};

function interpolate(template: string, values?: Record<string, unknown>) {
  if (!values) {
    return template;
  }
  return template.replace(INTERPOLATION_REGEX, (match, key) => (key in values ? String(values[key]) : match));
}
